/**
 * NewTransferPage - 6-step warranty ownership transfer wizard (Phase 1a).
 *
 * Steps:
 *   1. Property Lookup - ZIP-first search, narrow by name/email when needed
 *   2. Eligibility & Registrations - property summary + eligibility pill + pick holdings
 *   3. New Owner + Type + Closing Date (3-in-1) - with 60/100-day override modal
 *   4. Documents - proof of ownership (upload now or email new owner)
 *   5. Fee & Payment - auto-populated from warranty template
 *   6. Review - summary + interaction notes + submit
 *
 * On submit → redirect to /transfers/:uuid detail page.
 */

import { useState, useCallback, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { AppShell } from '@/admin/components/app-shell'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { PhoneInput } from '@/shared/components/ui/phone-input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Badge } from '@/shared/components/ui/badge'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { DatePicker } from '@/shared/components/ui/date-picker'
import { AlertTriangle } from 'lucide-react'
import {
  DocumentUploadInput,
  type DocumentSource,
} from '@/admin/components/document-upload-input'
import type { ResolvedAddress } from '@/admin/hooks/use-lookups'
import {
  useCreateTransfer,
  useEligibilityPreflight,
  useHostEligibility,
  useStructureEligibility,
  useTransferTypes,
  type CreateTransferPayload,
  type EligibilityPreflightResult,
  type EligibilityViolation,
  type StructureEligibilityHolding,
  type TransferReason,
} from '@/admin/hooks/use-transfers'
import { useHoldings } from '@/admin/hooks/use-products'
import { formatDurationFromMonths } from '@/admin/lib/formatting'
import type { Holding } from '@/admin/types/products'
import { cn } from '@/shared/lib/utils'
import { toastApiError } from '@/shared/lib/api-error'
import { useFormatters } from '@/shared/lib/formatting'
import { HostSearchInput } from '@/admin/components/shared/host-search-input'
import { AddressCapture } from '@/admin/components/registration-wizard/address-capture'
import { PropertyEligibilityPanel } from './components/property-eligibility-panel'
import { OverrideWarningModal } from './components/override-warning-modal'
import { FeePanel } from './components/fee-panel'
import {
  computeEligibility,
  type EligibilityResult,
} from './components/eligibility'
import { warrantyTypeLabel } from './components/warranty-label'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Pull eligibility violations out of a 409 submit error. Backend shape:
 *   { status, message, result: { violations: [...], requires_exception: true } }
 * Returns ``null`` if the error isn't a transfer-eligibility 409.
 */
function extractEligibilityViolations(
  err: unknown,
): EligibilityViolation[] | null {
  if (!err || typeof err !== 'object') return null
  const axiosErr = err as { response?: { data?: unknown } }
  const data = axiosErr.response?.data
  if (!data || typeof data !== 'object') return null
  const record = data as Record<string, unknown>
  const result = record.result ?? record.data
  if (!result || typeof result !== 'object') return null
  const violations = (result as Record<string, unknown>).violations
  if (!Array.isArray(violations)) return null
  return violations as EligibilityViolation[]
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TransferWizardState {
  stepKey: StepKey
  // Step 1 - Property
  selectedHostUuid: string | null
  /**
   * Set when the wizard is deep-linked from a registration that lives
   * on a Structure (multi-building real-estate path) - the warranty
   * record page passes ``?structure=<uuid>``. Drives the structure-
   * scope eligibility fetch in step 2 so cancelled / non-transferable
   * registrations on the same structure surface with reason chips
   * (instead of being silently filtered out).
   */
  selectedStructureUuid: string | null
  hostDetails: ResolvedAddress | null
  hostDisplay: string | null
  // Step 2 - Holdings
  selectedHoldingUuids: string[]
  // Step 3 - New owner + type + closing date
  newOwnerFirstName: string
  newOwnerLastName: string
  newOwnerEmail: string
  newOwnerPhone: string
  newOwnerMailingAddress: ResolvedAddress | null
  mailingSameAsProperty: boolean
  propertyClosingDate: string
  transferType: string
  override100DayFlag: boolean
  overrideReason: string
  // Step 4 - Documents
  proofDocument: DocumentSource
  emailUploadLink: boolean
  // When ``emailUploadLink`` is true, these carry the editable recipient + Cc +
  // note the agent can tweak before sending. ``docsEmailRecipient`` is seeded
  // from ``newOwnerEmail`` the first time the agent switches into email mode,
  // but is then independent - editing the "To" field on this draft must never
  // overwrite the new owner's email on the transfer record. §10: the note
  // (``docsEmailBody``) + Cc are posted with the transfer so the documents-
  // request email actually sent matches the preview on screen (subject + the
  // rest of the body come from the platform documents-request template, shown
  // read-only in the preview - so what the agent sees is what ships).
  docsEmailRecipient: string
  // §6: when the docs recipient is changed to an address that is NOT the new
  // owner's, it is a distinct person - capture their name so the contact is
  // named correctly (not a clone of the new owner) and set as main contact.
  docsRecipientFirstName: string
  docsRecipientLastName: string
  docsEmailCc: string
  docsEmailBody: string
  // Step 5 - Fee & payment
  feeWaived: boolean
  feeWaivedReason: string
  paymentMethod: string
  // Step 6 - Review
  interactionNotes: string
}

const emptyDocument: DocumentSource = {
  uploaded_file_uuid: null,
  uploaded_file_name: null,
  url: null,
}

const initialState: TransferWizardState = {
  stepKey: 'property',
  selectedHostUuid: null,
  selectedStructureUuid: null,
  hostDetails: null,
  hostDisplay: null,
  selectedHoldingUuids: [],
  newOwnerFirstName: '',
  newOwnerLastName: '',
  newOwnerEmail: '',
  newOwnerPhone: '',
  newOwnerMailingAddress: null,
  mailingSameAsProperty: true,
  propertyClosingDate: '',
  transferType: 'sale',
  override100DayFlag: false,
  overrideReason: '',
  proofDocument: emptyDocument,
  emailUploadLink: false,
  docsEmailRecipient: '',
  docsRecipientFirstName: '',
  docsRecipientLastName: '',
  docsEmailCc: '',
  docsEmailBody: '',
  feeWaived: false,
  feeWaivedReason: '',
  paymentMethod: '',
  interactionNotes: '',
}

/**
 * The wizard's step set is DYNAMIC, driven by the selected transfer
 * type's ``requires_fee`` / ``requires_documentation`` flags (see
 * ``useTransferTypes`` in ``use-transfers.ts``). ``ALL_STEP_DEFS`` is
 * the full ordered catalogue; ``NewTransferWizard`` filters it down to
 * the steps that apply to the currently-selected type. Never hardcode
 * transfer-type codes to decide this - the flags are the source of
 * truth so a tenant can add/reconfigure types without a frontend change.
 */
type StepKey =
  | 'property'
  | 'eligibility'
  | 'owner'
  | 'documents'
  | 'fee'
  | 'review'

interface StepDef {
  key: StepKey
  label: string
}

const ALL_STEP_DEFS: StepDef[] = [
  { key: 'property', label: 'Property' },
  { key: 'eligibility', label: 'Eligibility' },
  { key: 'owner', label: 'Owner & Closing' },
  { key: 'documents', label: 'Documents' },
  { key: 'fee', label: 'Fee' },
  { key: 'review', label: 'Review' },
]

// Fallback transfer-type options, used ONLY while ``useTransferTypes()``
// is loading or returns an empty catalogue. Real environments always
// have server-configured types; the flags below intentionally imply
// "keep every step" (fee + documentation both required) since there's
// no data-driven signal to gate on yet.
const TRANSFER_TYPES = [
  { value: 'sale', label: 'Sale' },
  { value: 'contingency', label: 'Contingency' },
  { value: 'inheritance', label: 'Inheritance' },
  { value: 'name_change', label: 'Name Change' },
  { value: 'builder_first_year', label: 'Builder First Year' },
]

function isTransferActiveHolding(holding: Holding): boolean {
  const rawStatus = (holding.status ?? '').toLowerCase()
  if (rawStatus === 'cancelled' || rawStatus === 'transferred') return false

  const lifecycleState = holding.workflow_states
    ?.find((state) => state.workflow_key === 'lifecycle_status')
    ?.state_key?.toLowerCase()

  if (lifecycleState) {
    return lifecycleState === 'active' || lifecycleState === 'registered'
  }

  return rawStatus === 'registered'
}

// ---------------------------------------------------------------------------
// Step 1: Property Lookup (ZIP-first)
// ---------------------------------------------------------------------------

function StepPropertyLookup({
  state,
  update,
  next,
}: {
  state: TransferWizardState
  update: (p: Partial<TransferWizardState>) => void
  next: () => void
}) {
  return (
    <>
      {!state.selectedHostUuid ? (
        <HostSearchInput
          onSelect={(host) => {
            // Build the same `ResolvedAddress`-shaped object the
            // wizard's downstream code already consumes - keeps the
            // host-detail rendering and review-step copy unchanged.
            const v = host.address?.value
            const display =
              host.address?.display ||
              [v?.address_line_1, v?.postal_code].filter(Boolean).join(' · ')
            update({
              selectedHostUuid: host.uuid,
              hostDisplay: display || host.display_name,
              hostDetails: {
                line_1: v?.address_line_1 ?? '',
                line_2: v?.address_line_2 ?? '',
                town_or_city: v?.locality ?? '',
                county: v?.region ?? '',
                postcode: v?.postal_code ?? '',
                country: v?.country_code ?? '',
              },
            })
            // Advance automatically - property selected is a clear intent
            setTimeout(next, 200)
          }}
        />
      ) : (
        <div className="max-w-[560px]">
          <h2 className="mb-1 font-heading text-[20px] text-[var(--ink)]">
            Property Selected
          </h2>
          <div className="mb-6 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5">
            <p className="clara-section-label mb-2">Property</p>
            {state.hostDetails && (
              <>
                <p className="text-[14px] text-[var(--ink)]">
                  {state.hostDetails.line_1}
                </p>
                {state.hostDetails.line_2 && (
                  <p className="text-[13px] text-[var(--ink-2)]">
                    {state.hostDetails.line_2}
                  </p>
                )}
                <p className="text-[13px] text-[var(--ink-2)]">
                  {state.hostDetails.town_or_city}
                  {state.hostDetails.county
                    ? `, ${state.hostDetails.county}`
                    : ''}{' '}
                  {state.hostDetails.postcode}
                </p>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() =>
                update({
                  selectedHostUuid: null,
                  hostDetails: null,
                  hostDisplay: null,
                  selectedHoldingUuids: [],
                })
              }
            >
              Change Property
            </Button>
            <Button onClick={next}>Continue</Button>
          </div>
        </div>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Step 2: Eligibility + Registrations
// ---------------------------------------------------------------------------

function StepEligibility({
  state,
  update,
  next,
  back,
  preflight,
  preflightLoading,
}: {
  state: TransferWizardState
  update: (p: Partial<TransferWizardState>) => void
  next: () => void
  back: () => void
  preflight: EligibilityPreflightResult | null
  preflightLoading: boolean
}) {
  // ``eligibilityMode`` covers BOTH structure and host entries - both
  // paths now hit a server-authoritative eligibility endpoint that
  // returns ALL holdings on the subject (active + ineligible) with a
  // chip-ready reason code per row. Address-search-only entries
  // (no host param yet) keep the legacy active-only flow.
  const eligibilityMode =
    !!state.selectedStructureUuid || !!state.selectedHostUuid
  const params = useMemo(() => {
    const p: Record<string, string> = { page_size: '50' }
    if (state.selectedStructureUuid) {
      p.structure_uuid = state.selectedStructureUuid
    } else if (state.selectedHostUuid) {
      // No status filter - render every holding under the host so
      // cancelled / non-transferable rows surface with a reason chip.
      p.host_uuid = state.selectedHostUuid
    } else {
      p.status = 'active'
    }
    return p
  }, [state.selectedHostUuid, state.selectedStructureUuid])

  const { data, isLoading } = useHoldings(params)
  const allHoldings = useMemo(() => data?.results ?? [], [data])

  // Server-authoritative per-holding eligibility. The transfer window is
  // measured from the property SALE/closing date, which we don't know
  // until the agent enters it at step 3. Default to today (not the
  // registration's purchase_date) for the initial render so an older
  // registration isn't wrongly flagged "transfer window passed" before
  // the agent has entered a real closing date. The query re-fires with
  // the true closing date once it's supplied.
  const effectiveClosingDate =
    state.propertyClosingDate || new Date().toLocaleDateString('en-CA')
  const structureEligibilityQuery = useStructureEligibility(
    state.selectedStructureUuid,
    state.transferType,
    effectiveClosingDate,
  )
  const hostEligibilityQuery = useHostEligibility(
    state.selectedStructureUuid ? null : state.selectedHostUuid,
    state.transferType,
    effectiveClosingDate,
  )
  const eligibilityQuery = state.selectedStructureUuid
    ? structureEligibilityQuery
    : hostEligibilityQuery
  const eligibilityByHoldingUuid = useMemo(() => {
    const map = new Map<string, StructureEligibilityHolding>()
    for (const row of eligibilityQuery.data?.holdings ?? []) {
      map.set(row.uuid, row)
    }
    return map
  }, [eligibilityQuery.data])

  // In eligibility mode (structure OR host) keep ALL holdings on
  // screen so cancelled / not-transferable rows surface with a reason
  // chip. In legacy address-search mode (no subject yet) preserve
  // pre-feature behaviour (only transferable rows render).
  const holdings = useMemo(
    () =>
      eligibilityMode
        ? allHoldings
        : allHoldings.filter(isTransferActiveHolding),
    [allHoldings, eligibilityMode],
  )

  // Auto-select the eligible-only subset. Backend uses this to populate
  // the transfer (eligible rows go through normal flow, ineligible ones
  // are recorded as auto-void on the resulting transfer record).
  const eligibleHoldings = useMemo(() => {
    if (!eligibilityMode) return holdings.filter(isTransferActiveHolding)
    return holdings.filter((h) => {
      const row = eligibilityByHoldingUuid.get(h.uuid)
      return row ? row.is_eligible : false
    })
  }, [holdings, eligibilityMode, eligibilityByHoldingUuid])

  // When a property is sold, every ELIGIBLE registration moves with the
  // sale - keep the selection in sync. Ineligible rows are explicitly
  // excluded from the selection (server records them as auto-void).
  useEffect(() => {
    if (eligibleHoldings.length === 0) return
    const uuids = eligibleHoldings.map((h) => h.uuid)
    const current = state.selectedHoldingUuids
    const same =
      current.length === uuids.length &&
      current.every((u) => uuids.includes(u))
    if (!same) update({ selectedHoldingUuids: uuids })
  }, [eligibleHoldings, state.selectedHoldingUuids, update])

  const ineligibleCount = holdings.length - eligibleHoldings.length

  const nonTransferableCount = eligibleHoldings.reduce((acc, h) => {
    const hasBlock = (h.warranties ?? []).some((w) => w.is_transferable === false)
    return acc + (hasBlock ? 1 : 0)
  }, 0)

  const canProceed = eligibleHoldings.length > 0

  return (
    <div className="max-w-[920px]">
      <h2 className="mb-1 font-heading text-[20px] text-[var(--ink)]">
        Warranty Registration Record
      </h2>
      <p className="mb-6 text-[13px] text-[var(--ink-3)]">
        Every warranty registration on this property transfers together with
        the sale. Review coverage, transferability and the eligibility window
        below before continuing.
      </p>

      {/* Eligibility panel - property, rules, decision */}
      <div className="mb-6">
        <PropertyEligibilityPanel
          hostAddress={state.hostDetails}
          hostDisplay={state.hostDisplay}
          closingDate={effectiveClosingDate}
          holdings={holdings}
          preflight={preflight}
          preflightLoading={preflightLoading}
        />
      </div>

      {/* Auto-void warning - only meaningful when AT LEAST ONE
          registration is eligible (otherwise there's no transfer for
          the auto-voids to ride along with). When everything is
          ineligible, the empty-state banner below explains the situ-
          ation; we don't promise an approval that can't happen. */}
      {eligibilityMode && ineligibleCount > 0 && eligibleHoldings.length > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-[6px] border-[0.5px] border-[var(--status-warn)] bg-[var(--status-warn-bg)] p-3">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--status-warn)]" />
          <p className="text-[12px] leading-relaxed text-[var(--ink-2)]">
            {ineligibleCount}{' '}
            {ineligibleCount === 1 ? 'registration is' : 'registrations are'}{' '}
            <span className="font-medium">not eligible</span> for transfer (see
            reason below per row). They&apos;ll be voided automatically when
            this transfer is approved.
          </p>
        </div>
      )}

      {/* Non-transferable warning (host-mode legacy / additional context) */}
      {!eligibilityMode && nonTransferableCount > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-[6px] border-[0.5px] border-[var(--status-warn)] bg-[var(--status-warn-bg)] p-3">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--status-warn)]" />
          <p className="text-[12px] leading-relaxed text-[var(--ink-2)]">
            {nonTransferableCount}{' '}
            {nonTransferableCount === 1 ? 'warranty is' : 'warranties are'}{' '}
            marked <span className="font-medium">non-transferable</span> by the
            warranty template. The transfer will still be recorded, but those
            warranties will not transfer to the new owner without an exception.
          </p>
        </div>
      )}

      {/* Warranty registration rows */}
      <div className="mb-6">
        <div className="mb-3 flex items-baseline justify-between gap-3">
          <p className="clara-section-label">
            Registrations on this property ({holdings.length})
          </p>
        </div>

        {/* Property-level transfers move EVERY ELIGIBLE registration
            on the host/structure together. Ineligible rows are listed
            for transparency and voided on approval. */}
        {!isLoading && eligibleHoldings.length > 0 && (
          <p className="mb-3 text-[12px] text-[var(--ink-2)]">
            {eligibleHoldings.length} eligible registration
            {eligibleHoldings.length === 1 ? ' moves' : 's move'} with the
            property sale and will be linked to this transfer record.
            {!eligibilityMode && nonTransferableCount > 0 && (
              <>
                {' '}
                Coverage on the{' '}
                <span className="font-medium">
                  {nonTransferableCount} non-transferable
                </span>{' '}
                {nonTransferableCount === 1 ? 'warranty' : 'warranties'} does
                not carry over to the new owner unless an approver grants an
                exception - see the badge on each row below.
              </>
            )}
          </p>
        )}

        {isLoading && (
          <p className="text-[13px] text-[var(--ink-3)]">
            Loading registration record...
          </p>
        )}
        {!isLoading && holdings.length === 0 && (
          <div className="rounded-[8px] border-[0.5px] border-dashed border-[var(--rule-strong)] bg-[var(--bg-subtle)] p-6 text-center">
            <p className="text-[13px] text-[var(--ink-3)]">
              No registrations found for this property.
            </p>
          </div>
        )}
        {!isLoading &&
          eligibilityMode &&
          holdings.length > 0 &&
          eligibleHoldings.length === 0 && (
            <div className="mb-3 rounded-[8px] border-[0.5px] border-dashed border-[var(--rule-strong)] bg-[var(--bg-subtle)] p-4 text-center">
              <p className="text-[13px] text-[var(--ink-3)]">
                None of the registrations on this property are currently
                eligible for transfer. See the reason chip on each row below.
              </p>
            </div>
          )}

        <div className="space-y-2">
          {holdings.map((h: Holding) => (
            <WarrantyRegistrationRow
              key={h.uuid}
              holding={h}
              eligibility={
                eligibilityMode ? eligibilityByHoldingUuid.get(h.uuid) : undefined
              }
            />
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={back}>
          Back
        </Button>
        <Button onClick={next} disabled={!canProceed}>
          Continue
        </Button>
      </div>
    </div>
  )
}

const VOID_REASON_LABEL: Record<string, string> = {
  not_active: 'Not active',
  not_transferable: 'Not transferable',
  coverage_expired: 'Coverage expired',
  transfer_window_passed: 'Window passed',
  prior_transfer_limit: 'Transfer limit reached',
}

function WarrantyRegistrationRow({
  holding,
  eligibility,
}: {
  holding: Holding
  eligibility?: StructureEligibilityHolding
}) {
  const { formatDate } = useFormatters()
  const warranty = holding.warranties?.[0]
  const typeLabel = warranty ? warrantyTypeLabel(warranty.warranty_type) : null
  const duration = warranty
    ? warranty.is_lifetime
      ? 'Lifetime'
      : formatDurationFromMonths(warranty.duration_in_months)
    : ''

  const ineligible = eligibility ? !eligibility.is_eligible : false
  const reasonLabel =
    eligibility && eligibility.void_reason_code
      ? VOID_REASON_LABEL[eligibility.void_reason_code] ?? 'Ineligible'
      : null

  return (
    <div
      className={cn(
        'rounded-[8px] border-[0.5px] p-4',
        ineligible
          ? 'border-[var(--rule)] bg-[var(--bg-subtle)] opacity-90'
          : 'border-[var(--rule)] bg-[var(--bg)]',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="text-[14px] font-medium text-[var(--ink)]">
            {holding.product.cached_short_name}
          </p>
          <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
            Registered to {holding.consumer_account.first_name}{' '}
            {holding.consumer_account.last_name}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {typeLabel && (
            <Badge
              variant="secondary"
              className="rounded-full bg-[var(--bg-subtle)] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.08em] text-[var(--ink-2)]"
            >
              {typeLabel}
            </Badge>
          )}
          {warranty?.is_transferable === false && !ineligible && (
            <Badge
              variant="secondary"
              title="This warranty's coverage stays with the original owner unless an approver grants an exception. The registration still appears on the transfer record for audit."
              className="rounded-full bg-[var(--status-warn-bg)] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.08em] text-[var(--status-warn)]"
            >
              Non-transferable
            </Badge>
          )}
          {ineligible && reasonLabel ? (
            <Badge
              variant="secondary"
              title={
                eligibility?.void_reason_detail ||
                'This registration is not eligible for transfer.'
              }
              className="rounded-full bg-[var(--status-warn-bg)] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.08em] text-[var(--status-warn)]"
            >
              {reasonLabel}
            </Badge>
          ) : (
            <Badge
              variant="secondary"
              className="rounded-full bg-[var(--status-ok-bg)] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.08em] text-[var(--status-ok)]"
            >
              {holding.status ?? 'ACTIVE'}
            </Badge>
          )}
        </div>
      </div>
      {ineligible && eligibility?.void_reason_detail && (
        <p className="mt-2 text-[11px] text-[var(--ink-3)]">
          {eligibility.void_reason_detail}
        </p>
      )}

      {warranty && (
        <div className="mt-3 grid grid-cols-2 gap-3 border-t-[0.5px] border-t-[var(--rule)] pt-3 md:grid-cols-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              Class
            </p>
            <p className="mt-0.5 text-[12px] text-[var(--ink)]">
              {warranty.class_name || '-'}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              Coverage
            </p>
            <p className="mt-0.5 text-[12px] text-[var(--ink)]">
              {duration || '-'}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              Active From
            </p>
            <p className="mt-0.5 text-[12px] text-[var(--ink)]">
              {warranty.active_from
                ? formatDate(warranty.active_from)
                : '-'}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              Prior Transfers
            </p>
            <p className="mt-0.5 text-[12px] text-[var(--ink)]">
              {holding.transfer_count ?? 0}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 3: New Owner + Transfer Type + Closing Date (3-in-1)
// ---------------------------------------------------------------------------

function StepOwnerAndClosing({
  state,
  update,
  next,
  back,
  preflight,
  preflightLoading,
  typeOptions,
}: {
  state: TransferWizardState
  update: (p: Partial<TransferWizardState>) => void
  next: () => void
  back: () => void
  preflight: EligibilityPreflightResult | null
  preflightLoading: boolean
  typeOptions: { value: string; label: string }[]
}) {
  const { t } = useTranslation()
  const [modalOpen, setModalOpen] = useState(false)
  // Per the house rule, Continue stays enabled even when required fields are
  // empty; clicking it validates and highlights what's missing rather than
  // stranding the user behind a disabled button.
  const [errors, setErrors] = useState<{
    firstName?: string
    lastName?: string
    transferType?: string
    closingDate?: string
  }>({})

  const clearError = (field: keyof typeof errors) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev))
  }

  const eligibility: EligibilityResult = computeEligibility({
    closingDate: state.propertyClosingDate || null,
    preflight,
    preflightLoading,
  })

  const handleContinue = () => {
    // Validate required fields first; surface a per-field error and stay on the
    // step rather than silently no-oping, so the agent sees exactly what's
    // missing.
    const nextErrors: typeof errors = {}
    if (state.newOwnerFirstName.trim() === '')
      nextErrors.firstName = t(
        'transfers.wizard.firstNameRequired',
        'First name is required',
      )
    if (state.newOwnerLastName.trim() === '')
      nextErrors.lastName = t(
        'transfers.wizard.lastNameRequired',
        'Last name is required',
      )
    if (state.transferType === '')
      nextErrors.transferType = t(
        'transfers.wizard.transferTypeRequired',
        'Transfer type is required',
      )
    if (state.propertyClosingDate === '')
      nextErrors.closingDate = t(
        'transfers.wizard.closingDateRequired',
        'Property closing date is required',
      )
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }
    setErrors({})

    // Open override modal if the server returned BLOCK violations and
    // the agent hasn't confirmed the override yet.
    if (eligibility.state === 'override' && !state.override100DayFlag) {
      setModalOpen(true)
      return
    }
    next()
  }

  const handleClosingDateChange = (v: string) => {
    update({
      propertyClosingDate: v,
      override100DayFlag: false,
      overrideReason: '',
    })
    // Preflight fires via the parent's useEffect on closing-date change;
    // we just open the modal if the most-recent result was blocking.
    // (Same staleness characteristics as the old native-input onBlur
    // handler this replaces - the authoritative gate re-runs on Continue.)
    if (eligibility.state === 'override' && !state.override100DayFlag) {
      setModalOpen(true)
    }
  }

  return (
    <div className="max-w-[680px]">
      <h2 className="mb-1 font-heading text-[20px] text-[var(--ink)]">
        New Owner &amp; Closing Details
      </h2>
      <p className="mb-6 text-[13px] text-[var(--ink-3)]">
        Capture the new owner&rsquo;s details and the property closing date.
      </p>

      {/* Name */}
      <div className="mb-4 grid grid-cols-2 gap-4">
        <div>
          <Label
            className={`clara-section-label mb-1.5 block ${errors.firstName ? 'text-[var(--status-error)]' : ''}`}
          >
            First name *
          </Label>
          <Input
            value={state.newOwnerFirstName}
            onChange={(e) => {
              update({ newOwnerFirstName: e.target.value })
              clearError('firstName')
            }}
            placeholder="First name"
            data-testid="new-owner-first-name"
            aria-invalid={errors.firstName ? true : undefined}
            className={
              errors.firstName
                ? 'border-[var(--status-error)] ring-1 ring-[var(--status-error)]'
                : undefined
            }
          />
          {errors.firstName && (
            <p className="mt-1 text-[11px] text-[var(--status-error)]">
              {errors.firstName}
            </p>
          )}
        </div>
        <div>
          <Label
            className={`clara-section-label mb-1.5 block ${errors.lastName ? 'text-[var(--status-error)]' : ''}`}
          >
            Last name *
          </Label>
          <Input
            value={state.newOwnerLastName}
            onChange={(e) => {
              update({ newOwnerLastName: e.target.value })
              clearError('lastName')
            }}
            placeholder="Last name"
            data-testid="new-owner-last-name"
            aria-invalid={errors.lastName ? true : undefined}
            className={
              errors.lastName
                ? 'border-[var(--status-error)] ring-1 ring-[var(--status-error)]'
                : undefined
            }
          />
          {errors.lastName && (
            <p className="mt-1 text-[11px] text-[var(--status-error)]">
              {errors.lastName}
            </p>
          )}
        </div>
      </div>

      {/* Contact hierarchy - email first, mailing second, phone last (Beth's
          call-center flow). Each tier is labelled so the agent knows which
          channel the Champion + automation treat as primary. */}
      <div className="mb-6 flex flex-col gap-4">
        {/* Tier 1 - Email */}
        <div>
          <div className="mb-1.5 flex items-baseline justify-between gap-3">
            <Label className="clara-section-label">Email</Label>
          </div>
          <Input
            type="email"
            value={state.newOwnerEmail}
            onChange={(e) => update({ newOwnerEmail: e.target.value })}
            placeholder="email@example.com"
            data-testid="new-owner-email"
          />
          <p className="mt-1 text-[11px] text-[var(--ink-3)]">
            All proof-of-ownership and decision notifications go here.
          </p>
        </div>

        {/* Tier 2 - Mailing address (with same-as-property toggle) */}
        <div>
          <div className="mb-1.5 flex items-baseline justify-between gap-3">
            <Label className="clara-section-label">Mailing address</Label>
          </div>
          <label className="mb-2 flex cursor-pointer items-center gap-2 text-[12px] text-[var(--ink-2)]">
            <Checkbox
              checked={state.mailingSameAsProperty}
              onCheckedChange={(v) =>
                update({
                  mailingSameAsProperty: v === true,
                  ...(v === true ? { newOwnerMailingAddress: null } : {}),
                })
              }
              data-testid="mailing-same-as-property"
            />
            Same as property address
          </label>
          {state.mailingSameAsProperty ? (
            <div className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule-strong)] bg-[var(--bg-subtle)] px-3 py-2 text-[12px] text-[var(--ink-2)]">
              {state.hostDetails
                ? [
                    state.hostDetails.line_1,
                    state.hostDetails.line_2,
                    state.hostDetails.town_or_city,
                    state.hostDetails.postcode,
                  ]
                    .filter(Boolean)
                    .join(', ')
                : state.hostDisplay ?? 'Property address from step 1'}
            </div>
          ) : (
            // Reuses the registration-wizard's address-capture flow
            // (``useAddressAutocomplete``) so the
            // new owner's mailing address gets the same fuzzy lookup
            // + structured resolve the rest of the platform uses.
            <AddressCapture
              value={state.newOwnerMailingAddress}
              onChange={(next) => update({ newOwnerMailingAddress: next })}
              testId="mailing-address-capture"
              summaryLabel="Mailing address"
              placeholder="Start typing the new owner's address or postcode"
            />
          )}
        </div>

        {/* Tier 3 - Phone */}
        <div>
          <div className="mb-1.5 flex items-baseline justify-between gap-3">
            <Label className="clara-section-label">Phone</Label>
          </div>
          <PhoneInput
            value={state.newOwnerPhone}
            onChange={(v) => update({ newOwnerPhone: v })}
            placeholder="(555) 123-4567"
            hideExtension
          />
        </div>
      </div>

      {/* Transfer type + closing date */}
      <div className="mb-6 grid grid-cols-2 gap-4">
        <div>
          <Label
            className={`clara-section-label mb-1.5 block ${errors.transferType ? 'text-[var(--status-error)]' : ''}`}
          >
            Transfer type *
          </Label>
          <Select
            value={state.transferType}
            onValueChange={(v: string) => {
              update({ transferType: v })
              clearError('transferType')
            }}
          >
            <SelectTrigger
              className={`w-full ${errors.transferType ? 'border-[var(--status-error)] ring-1 ring-[var(--status-error)]' : ''}`}
              aria-invalid={errors.transferType ? true : undefined}
            >
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {typeOptions.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.transferType && (
            <p className="mt-1 text-[11px] text-[var(--status-error)]">
              {errors.transferType}
            </p>
          )}
        </div>
        <div>
          <Label
            className={`clara-section-label mb-1.5 block ${errors.closingDate ? 'text-[var(--status-error)]' : ''}`}
          >
            Property closing date *
          </Label>
          <DatePicker
            value={state.propertyClosingDate}
            onChange={(v) => {
              handleClosingDateChange(v)
              clearError('closingDate')
            }}
            placeholder="Select closing date"
            data-testid="closing-date-input"
            className={
              errors.closingDate
                ? 'border-[var(--status-error)] ring-1 ring-[var(--status-error)]'
                : undefined
            }
          />
          {errors.closingDate && (
            <p className="mt-1 text-[11px] text-[var(--status-error)]">
              {errors.closingDate}
            </p>
          )}
        </div>
      </div>

      {/* Eligibility summary inline */}
      {state.propertyClosingDate && (
        <div
          className={`mb-6 rounded-[6px] border-[0.5px] p-3 text-[12px] ${
            eligibility.state === 'eligible'
              ? 'border-[var(--status-ok-bg)] bg-[var(--status-ok-bg)]/30 text-[var(--status-ok)]'
              : eligibility.state === 'override'
                ? 'border-[var(--status-warn-bg)] bg-[var(--status-warn-bg)]/30 text-[var(--status-warn)]'
                : 'border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-3)]'
          }`}
        >
          <span className="font-medium">{eligibility.label}</span>
          {' - '}
          {eligibility.helperText}
          {state.override100DayFlag && (
            <span className="mt-1 block text-[11px]">
              Override confirmed. Reason captured.
            </span>
          )}
        </div>
      )}

      {/* Override modal */}
      <OverrideWarningModal
        open={modalOpen}
        state={eligibility.state}
        daysSinceClosing={eligibility.daysSinceClosing}
        blockingViolations={eligibility.blockingViolations}
        onCancel={() => setModalOpen(false)}
        onConfirmOverride={(reason) => {
          update({ override100DayFlag: true, overrideReason: reason })
          setModalOpen(false)
        }}
      />

      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={back}>
          Back
        </Button>
        <Button onClick={handleContinue} data-testid="step-owner-continue">
          Continue
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 4: Documents (proof of ownership)
// ---------------------------------------------------------------------------

/**
 * Compose the documents-request email on the documents step.
 *
 * §10 (WPS-157 comment 10797): the agent edits the recipient, Cc, and an
 * OPTIONAL note; the note is added to the top of the standard documents-request
 * email and posted with the transfer, so the email actually sent matches what
 * is shown here. The subject and the rest of the body come from the platform
 * documents-request template and are rendered read-only in a live preview - so
 * "what you see is what the customer receives" (the earlier draft showed a
 * different body than what shipped, which is the defect this fixes).
 *
 * ``to`` is the recipient for THIS email only - seeded from the new owner's
 * email once, then tracked independently (``docsEmailRecipient``) so editing it
 * never overwrites the new owner's email on the transfer record.
 */
function EditableDocsEmail({
  to,
  onToChange,
  cc,
  onCcChange,
  firstName,
  hostDisplay,
  body,
  onBodyChange,
  recipientIsNew,
  recipientFirstName,
  onRecipientFirstNameChange,
  recipientLastName,
  onRecipientLastNameChange,
}: {
  to: string
  onToChange: (v: string) => void
  cc: string
  onCcChange: (v: string) => void
  firstName: string
  hostDisplay: string | null
  body: string
  onBodyChange: (v: string) => void
  // §6: the recipient email is not the new owner's - a distinct person.
  recipientIsNew: boolean
  recipientFirstName: string
  onRecipientFirstNameChange: (v: string) => void
  recipientLastName: string
  onRecipientLastNameChange: (v: string) => void
}) {
  // The greeting addresses the actual recipient: when the "To" is a different
  // person, use the name entered for them, not the new owner's.
  const greetingName = recipientIsNew ? recipientFirstName : firstName
  const propertyLabel = hostDisplay ?? 'your new property'

  // Cc is hidden until the agent asks for it - reveal once they click
  // "Add Cc", or automatically when a Cc value already exists (e.g. the
  // agent stepped back to Owner & Closing and returned here).
  const [ccVisible, setCcVisible] = useState(cc.trim() !== '')

  return (
    <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5">
      <p className="clara-section-label mb-3">Email draft</p>
      <div className="flex flex-col gap-3">
        <div>
          <div className="mb-1.5 flex items-baseline justify-between gap-3">
            <Label className="clara-section-label">To</Label>
            {!ccVisible && (
              <Button
                type="button"
                variant="link"
                size="xs"
                className="h-auto p-0 text-[11px] font-normal normal-case tracking-normal"
                onClick={() => setCcVisible(true)}
                data-testid="docs-email-add-cc"
              >
                Add Cc
              </Button>
            )}
          </div>
          <Input
            type="email"
            value={to}
            onChange={(e) => onToChange(e.target.value)}
            placeholder="email@example.com"
            data-testid="docs-email-to"
          />
        </div>
        {recipientIsNew && (
          <div className="grid grid-cols-2 gap-3 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-3">
            <div className="col-span-2">
              <p className="text-[11px] leading-relaxed text-[var(--ink-3)]">
                This is a different recipient from the new owner - they will be
                added as the main contact. Their name is optional; leave it blank
                if they are already an owner on the record.
              </p>
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">
                First name (optional)
              </Label>
              <Input
                value={recipientFirstName}
                onChange={(e) => onRecipientFirstNameChange(e.target.value)}
                placeholder="First name"
                data-testid="docs-recipient-first-name"
              />
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">
                Last name (optional)
              </Label>
              <Input
                value={recipientLastName}
                onChange={(e) => onRecipientLastNameChange(e.target.value)}
                placeholder="Last name"
                data-testid="docs-recipient-last-name"
              />
            </div>
          </div>
        )}
        {ccVisible && (
          <div>
            <Label className="clara-section-label mb-1.5 block">Cc</Label>
            <Input
              type="text"
              value={cc}
              onChange={(e) => onCcChange(e.target.value)}
              placeholder="cc@example.com, another@example.com"
              data-testid="docs-email-cc"
            />
            <p className="mt-1 text-[11px] text-[var(--ink-3)]">
              Separate multiple addresses with a comma.
            </p>
          </div>
        )}
        <div>
          <Label className="clara-section-label mb-1.5 block">
            Message to the new owner (optional)
          </Label>
          <textarea
            value={body}
            onChange={(e) => onBodyChange(e.target.value)}
            rows={4}
            placeholder="Add a personal note. It appears in the body of the email below."
            className="w-full resize-y rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[13px] leading-relaxed text-[var(--ink)] placeholder:text-[var(--ink-3)] focus:border-[var(--ink-3)] focus:outline-none"
            data-testid="docs-email-body"
          />
        </div>
      </div>

      {/* Live preview of exactly what the recipient receives - the note above is
          shown inline; the rest is the platform documents-request template
          (read-only). "What you see is what is sent" - the fix for the earlier
          draft-vs-sent mismatch. */}
      <div className="mt-4">
        <p className="clara-section-label mb-1.5">Preview</p>
        <div
          className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-4"
          data-testid="docs-email-preview"
        >
          <p className="mb-3 text-[11px] text-[var(--ink-3)]">
            Subject: Please upload your documents to complete the transfer for{' '}
            {propertyLabel}
          </p>
          <p className="mb-2 text-[14px] font-semibold text-[var(--ink)]">
            Upload your transfer documents
          </p>
          <p className="mb-2 text-[13px] leading-relaxed text-[var(--ink-2)]">
            Hi {greetingName || 'there'},
          </p>
          <p className="mb-2 text-[13px] leading-relaxed text-[var(--ink-2)]">
            To complete the warranty transfer for {propertyLabel}, we need a few
            documents from you.
          </p>
          {body.trim() !== '' && (
            <p className="mb-2 whitespace-pre-line text-[13px] leading-relaxed text-[var(--ink)]">
              {body}
            </p>
          )}
          <p className="mb-1 text-[13px] font-medium text-[var(--ink)]">
            What to send
          </p>
          <ul className="mb-3 list-disc pl-5 text-[13px] leading-relaxed text-[var(--ink-2)]">
            <li>
              Proof of Ownership (required) - title deed, closing statement, or
              sales record.
            </li>
            <li>
              Other supporting documents - anything else that helps verify the
              transfer.
            </li>
          </ul>
          <p className="mb-2 text-[13px] leading-relaxed text-[var(--ink-2)]">
            Use your private, secure upload link. It stays open until every
            required document is in.
          </p>
          <span className="inline-block rounded-[6px] bg-[var(--ink)] px-3 py-1.5 text-[12px] font-medium text-[var(--bg)]">
            Upload your documents
          </span>
        </div>
        <p className="mt-2 text-[11px] text-[var(--ink-3)]">
          This is the email the recipient receives when you submit. The secure
          upload link is generated on submission.
        </p>
      </div>
    </div>
  )
}

function StepDocuments({
  state,
  update,
  next,
  back,
}: {
  state: TransferWizardState
  update: (p: Partial<TransferWizardState>) => void
  next: () => void
  back: () => void
}) {
  const [mode, setMode] = useState<'upload' | 'email'>(
    state.emailUploadLink ? 'email' : 'upload',
  )

  // Proof-of-ownership is hard-required at intake. In upload mode that
  // means a file is attached; in email mode it means the agent has
  // composed a real outbound email (non-empty to / subject / body) so
  // the new owner has something to action. Transfer moves to
  // AWAITING_CUSTOMER_INFORMATION on submit in the email path.
  const hasFile =
    state.proofDocument.uploaded_file_uuid !== null ||
    !!state.proofDocument.url
  // §6: the recipient is a distinct person when the "To" is set to an address
  // that is not the new owner's - then they are seeded as the main contact.
  const docsRecipientIsNew =
    state.docsEmailRecipient.trim() !== '' &&
    state.docsEmailRecipient.trim().toLowerCase() !==
      state.newOwnerEmail.trim().toLowerCase()
  // §4: only the recipient email is required. The name is OPTIONAL - forcing the
  // agent to re-type a name for someone the system may already know (the old
  // owner) was redundant friction (Patrick). Provide it to name the contact, or
  // leave it blank and an admin can complete it later.
  const emailReady = state.docsEmailRecipient.trim() !== ''
  const canProceed = mode === 'upload' ? hasFile : emailReady

  // Continue stays enabled; clicking it validates and surfaces what's missing
  // rather than sitting disabled behind an incomplete step.
  const [error, setError] = useState<string | null>(null)
  const handleContinue = () => {
    if (!canProceed) {
      setError(
        mode === 'upload'
          ? 'Attach a proof-of-ownership file to continue.'
          : 'Add a recipient to continue.',
      )
      return
    }
    setError(null)
    next()
  }

  return (
    <div className="max-w-[640px]">
      <h2 className="mb-1 font-heading text-[20px] text-[var(--ink)]">
        Proof of Ownership
      </h2>
      <p className="mb-4 text-[13px] text-[var(--ink-3)]">
        Upload the title deed or other proof now, or send the new owner a
        secure link to upload later. Proof is required before the transfer can
        be approved.
      </p>
      <div className="mb-6 flex items-start gap-2 rounded-[6px] border-[0.5px] border-[var(--status-warn-bg)] bg-[var(--status-warn-bg)]/30 p-3">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--status-warn)]" />
        <p className="text-[12px] leading-relaxed text-[var(--ink-2)]">
          {mode === 'upload'
            ? 'Attach a PDF, JPG, or PNG of the title deed, purchase agreement, or closing statement. Required to continue.'
            : 'Compose the email to the new owner. All fields are required - the transfer will be held in Awaiting Customer Information until proof is received.'}
        </p>
      </div>

      {/* Mode toggle */}
      <div className="mb-6 flex gap-2 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-1">
        <Button
          variant={mode === 'upload' ? 'default' : 'ghost'}
          onClick={() => {
            setMode('upload')
            setError(null)
            update({ emailUploadLink: false })
          }}
          className="flex-1"
        >
          Upload now
        </Button>
        <Button
          variant={mode === 'email' ? 'default' : 'ghost'}
          onClick={() => {
            setMode('email')
            setError(null)
            update({
              emailUploadLink: true,
              proofDocument: emptyDocument,
              // Seed the recipient from the new owner's email the first
              // time we enter email mode. Once set, it's an independent
              // draft field - re-entering this mode never overwrites an
              // edit the agent already made.
              docsEmailRecipient:
                state.docsEmailRecipient || state.newOwnerEmail,
            })
          }}
          className="flex-1"
        >
          Email new owner
        </Button>
      </div>

      {/* Content */}
      {mode === 'upload' ? (
        <DocumentUploadInput
          label="Title deed / proof of ownership"
          value={state.proofDocument}
          onChange={(v) => {
            setError(null)
            update({ proofDocument: v })
          }}
        />
      ) : (
        <EditableDocsEmail
          to={state.docsEmailRecipient}
          onToChange={(v) => {
            setError(null)
            update({ docsEmailRecipient: v })
          }}
          cc={state.docsEmailCc}
          onCcChange={(v) => update({ docsEmailCc: v })}
          firstName={state.newOwnerFirstName}
          hostDisplay={state.hostDisplay}
          body={state.docsEmailBody}
          onBodyChange={(v) => {
            setError(null)
            update({ docsEmailBody: v })
          }}
          recipientIsNew={docsRecipientIsNew}
          recipientFirstName={state.docsRecipientFirstName}
          onRecipientFirstNameChange={(v) => {
            setError(null)
            update({ docsRecipientFirstName: v })
          }}
          recipientLastName={state.docsRecipientLastName}
          onRecipientLastNameChange={(v) => {
            setError(null)
            update({ docsRecipientLastName: v })
          }}
        />
      )}

      {error && (
        <p className="mt-4 text-[12px] text-[var(--status-error)]">{error}</p>
      )}

      <div className="mt-6 flex items-center justify-between">
        <Button variant="ghost" onClick={back}>
          Back
        </Button>
        <Button onClick={handleContinue}>Continue</Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 5: Fee & Payment
// ---------------------------------------------------------------------------

function StepFee({
  state,
  update,
  next,
  back,
  baseFee,
}: {
  state: TransferWizardState
  update: (p: Partial<TransferWizardState>) => void
  next: () => void
  back: () => void
  baseFee: number
}) {

  // 3-way payment contract: when there is a fee, the agent must pick
  // one of {check, online, waive}. Waive additionally requires a
  // non-empty reason (mirrored on the backend serializer).
  const hasPaymentMethod =
    state.paymentMethod === 'check' || state.paymentMethod === 'online'
  const waiveReady =
    state.feeWaived && state.feeWaivedReason.trim().length > 0
  const canProceed = baseFee === 0 || hasPaymentMethod || waiveReady

  // Continue stays enabled; clicking it validates and surfaces what's missing.
  const [error, setError] = useState<string | null>(null)
  const handleContinue = () => {
    if (!canProceed) {
      setError(
        state.feeWaived
          ? 'Add a reason for waiving the fee to continue.'
          : 'Choose a payment method, or waive the fee with a reason, to continue.',
      )
      return
    }
    setError(null)
    next()
  }

  return (
    <>
      <FeePanel
        baseFee={baseFee}
        feeWaived={state.feeWaived}
        feeWaivedReason={state.feeWaivedReason}
        paymentMethod={state.paymentMethod}
        onFeeWaivedChange={(v) => {
          setError(null)
          update({
            feeWaived: v,
            ...(v ? {} : { feeWaivedReason: '' }),
          })
        }}
        onFeeWaivedReasonChange={(v) => {
          setError(null)
          update({ feeWaivedReason: v })
        }}
        onPaymentMethodChange={(v) => {
          setError(null)
          update({ paymentMethod: v })
        }}
      />
      {error && (
        <p className="mt-4 max-w-[640px] text-[12px] text-[var(--status-error)]">
          {error}
        </p>
      )}
      <div className="mt-6 flex max-w-[640px] items-center justify-between">
        <Button variant="ghost" onClick={back}>
          Back
        </Button>
        <Button onClick={handleContinue}>Continue</Button>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Step 6: Review + Submit
// ---------------------------------------------------------------------------

function StepReview({
  state,
  update,
  back,
  onSubmit,
  isSubmitting,
  preflight: preflightResult,
  preflightLoading,
  typeOptions,
  requiresFee,
  requiresDocumentation,
}: {
  state: TransferWizardState
  update: (p: Partial<TransferWizardState>) => void
  back: () => void
  onSubmit: () => Promise<void>
  isSubmitting: boolean
  preflight: EligibilityPreflightResult | null
  /** Surfaced for future loading-state UX; not currently consumed. */
  preflightLoading?: boolean
  typeOptions: { value: string; label: string }[]
  /** Drives whether the Fee & Payment summary card renders at all. */
  requiresFee: boolean
  /** Drives whether the Proof of Ownership summary card renders at all. */
  requiresDocumentation: boolean
}) {
  void preflightLoading
  const { formatDate } = useFormatters()
  // §2: the fee shown here is the backend-resolved policy fee (same value the
  // record snapshots), not a frontend heuristic - so the review and the saved
  // record never disagree.
  const baseFee = preflightResult?.resolved_policy
    ? Number(preflightResult.resolved_policy.fee_amount)
    : 0
  const transferTypeLabel =
    typeOptions.find((t) => t.value === state.transferType)?.label ??
    state.transferType

  // Preflight comes from the wizard parent (fires on closing-date /
  // holding / type change). Review step just reads the most recent
  // result and surfaces it inline before submit.
  const hasBlocks =
    !!preflightResult &&
    preflightResult.violations.some((v) => v.severity === 'BLOCK')
  const needsOverride = hasBlocks && !state.override100DayFlag

  // Submit stays enabled (except while a submit is in flight); clicking it
  // validates and highlights the interaction-notes field / surfaces the
  // override requirement rather than sitting disabled.
  const [notesError, setNotesError] = useState<string | null>(null)
  const handleSubmitClick = () => {
    if (state.interactionNotes.trim().length < 10) {
      setNotesError('Add at least 10 characters of interaction notes to submit.')
      return
    }
    setNotesError(null)
    // A blocking eligibility violation must be overridden first; the override
    // requirement is already surfaced inline in the Eligibility Issues card
    // above, so we just hold here rather than proceeding.
    if (needsOverride) return
    onSubmit()
  }

  return (
    <div className="max-w-[720px]">
      <h2 className="mb-1 font-heading text-[20px] text-[var(--ink)]">
        Review &amp; Submit
      </h2>
      <p className="mb-6 text-[13px] text-[var(--ink-3)]">
        Final check before submitting this transfer for Warranty Champion
        review.
      </p>

      <div className="space-y-4">
        {/* Property */}
        <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4">
          <p className="clara-section-label mb-2">Property</p>
          {state.hostDetails ? (
            <>
              <p className="text-[13px] text-[var(--ink)]">
                {state.hostDetails.line_1}
              </p>
              <p className="text-[12px] text-[var(--ink-2)]">
                {state.hostDetails.town_or_city},{' '}
                {state.hostDetails.postcode}
              </p>
            </>
          ) : (
            <p className="text-[13px] text-[var(--ink-2)]">
              {state.hostDisplay ?? '-'}
            </p>
          )}
          <p className="mt-2 text-[11px] text-[var(--ink-3)]">
            {state.selectedHoldingUuids.length} registration
            {state.selectedHoldingUuids.length !== 1 ? 's' : ''} selected
          </p>
        </div>

        {/* New owner */}
        <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4">
          <p className="clara-section-label mb-2">New Owner</p>
          <p className="text-[13px] text-[var(--ink)]">
            {state.newOwnerFirstName} {state.newOwnerLastName}
          </p>
          {state.newOwnerEmail && (
            <p className="text-[12px] text-[var(--ink-2)]">
              {state.newOwnerEmail}
            </p>
          )}
          {state.newOwnerPhone && (
            <p className="text-[12px] text-[var(--ink-2)]">
              {state.newOwnerPhone}
            </p>
          )}
        </div>

        {/* Transfer type + date */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4">
            <p className="clara-section-label mb-1">Transfer Type</p>
            <p className="text-[13px] text-[var(--ink)]">{transferTypeLabel}</p>
          </div>
          <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4">
            <p className="clara-section-label mb-1">Closing Date</p>
            <p className="text-[13px] text-[var(--ink)]">
              {state.propertyClosingDate
                ? formatDate(state.propertyClosingDate)
                : '-'}
            </p>
          </div>
        </div>

        {/* Override notice */}
        {state.override100DayFlag && (
          <div className="rounded-[8px] border-[0.5px] border-[var(--status-warn-bg)] bg-[var(--status-warn-bg)]/30 p-4">
            <p className="clara-section-label mb-1 text-[var(--status-warn)]">
              Eligibility Override Applied
            </p>
            <p className="text-[12px] text-[var(--ink-2)]">
              {state.overrideReason}
            </p>
          </div>
        )}

        {/* Documents - only when this transfer type requires documentation */}
        {requiresDocumentation && (
          <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4">
            <p className="clara-section-label mb-2">Proof of Ownership</p>
            {state.emailUploadLink ? (
              <>
                <p className="text-[13px] text-[var(--ink)]">
                  Email link will be sent to{' '}
                  {state.docsEmailRecipient || '(email required)'}
                </p>
                {state.docsEmailCc.trim() !== '' && (
                  <p className="mt-1 text-[12px] text-[var(--ink-2)]">
                    Cc: {state.docsEmailCc}
                  </p>
                )}
              </>
            ) : state.proofDocument.uploaded_file_name ? (
              <p className="text-[13px] text-[var(--ink)]">
                {state.proofDocument.uploaded_file_name}
              </p>
            ) : state.proofDocument.url ? (
              <p className="break-all text-[13px] text-[var(--ink)]">
                {state.proofDocument.url}
              </p>
            ) : (
              <p className="text-[13px] text-[var(--ink-3)]">No proof attached</p>
            )}
          </div>
        )}

        {/* Fee - only when this transfer type requires a fee */}
        {requiresFee && (
          <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4">
            <p className="clara-section-label mb-2">Fee &amp; Payment</p>
            {baseFee === 0 ? (
              <p className="text-[13px] text-[var(--ink)]">
                No fee (standard warranty)
              </p>
            ) : state.feeWaived ? (
              <>
                <p className="text-[13px] text-[var(--status-warn)]">
                  ${baseFee.toFixed(2)} - Waived
                </p>
                {state.feeWaivedReason && (
                  <p className="mt-1 text-[12px] text-[var(--ink-2)]">
                    {state.feeWaivedReason}
                  </p>
                )}
              </>
            ) : (
              <p className="text-[13px] text-[var(--ink)]">
                ${baseFee.toFixed(2)} -{' '}
                {state.paymentMethod.charAt(0).toUpperCase() +
                  state.paymentMethod.slice(1)}
              </p>
            )}
          </div>
        )}

        {/* Server-authoritative eligibility check - runs against the
            tenant's ValidationRules. Surfaces WARN / BLOCK inline so the
            agent sees issues before they click submit. */}
        {preflightResult && preflightResult.violations.length > 0 && (
          <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4">
            <div className="mb-2 flex items-center gap-2">
              <AlertTriangle
                className={
                  hasBlocks
                    ? 'h-3.5 w-3.5 text-[var(--status-error)]'
                    : 'h-3.5 w-3.5 text-[var(--status-warn)]'
                }
              />
              <p className="clara-section-label">
                {hasBlocks ? 'Eligibility Issues' : 'Eligibility Warnings'}
              </p>
            </div>
            <ul className="flex flex-col gap-1.5">
              {preflightResult.violations.map((v) => (
                <li key={v.rule_uuid} className="text-[12px]">
                  <span
                    className={
                      v.severity === 'BLOCK'
                        ? 'font-medium text-[var(--status-error)]'
                        : v.severity === 'WARN'
                          ? 'font-medium text-[var(--status-warn)]'
                          : 'font-medium text-[var(--ink-2)]'
                    }
                  >
                    [{v.severity}]
                  </span>{' '}
                  <span className="text-[var(--ink-2)]">{v.message}</span>
                </li>
              ))}
            </ul>
            {needsOverride && (
              <p className="mt-2 text-[12px] text-[var(--status-error)]">
                A blocking violation requires you to confirm the override
                warning before submitting.
              </p>
            )}
          </div>
        )}

        {/* Interaction notes */}
        <div>
          <Label
            className={`clara-section-label mb-1.5 block ${notesError ? 'text-[var(--status-error)]' : ''}`}
          >
            Interaction notes *
          </Label>
          <textarea
            value={state.interactionNotes}
            onChange={(e) => {
              update({ interactionNotes: e.target.value })
              if (notesError) setNotesError(null)
            }}
            rows={4}
            placeholder="Describe the call: who initiated, key details, anything unusual..."
            aria-invalid={notesError ? true : undefined}
            className={`w-full resize-y rounded-[6px] border-[0.5px] bg-[var(--bg)] px-3 py-2 text-[13px] text-[var(--ink)] placeholder:text-[var(--ink-3)] focus:outline-none ${
              notesError
                ? 'border-[var(--status-error)] ring-1 ring-[var(--status-error)] focus:border-[var(--status-error)]'
                : 'border-[var(--rule-strong)] focus:border-[var(--ink-3)]'
            }`}
            data-testid="interaction-notes"
          />
          {notesError ? (
            <p className="mt-1 text-[11px] text-[var(--status-error)]">
              {notesError}
            </p>
          ) : (
            <p className="mt-1 text-[11px] text-[var(--ink-3)]">
              Minimum 10 characters. These notes are permanent audit record.
            </p>
          )}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <Button variant="ghost" onClick={back}>
          Back
        </Button>
        <Button
          onClick={handleSubmitClick}
          disabled={isSubmitting}
          data-testid="submit-transfer"
        >
          {isSubmitting ? 'Submitting\u2026' : 'Submit for Review'}
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function NewTransferPage() {
  const [searchParams] = useSearchParams()
  // Deep-link seed: registration detail / list pages hand us the host or
  // structure via ``?host=<uuid>`` / ``?structure=<uuid>`` so agents don't
  // re-search for the property they just came from. The wizard initialises
  // its state ONCE from these seeds, so keep the reset logic here: keying the
  // inner wizard on the seed forces a full remount (and re-init) when the
  // agent navigates straight from one registration's Transfer action to
  // another's. Without the key, React reuses the mounted wizard and it keeps
  // the first property's structure/host - showing the wrong property and
  // submitting the wrong structure_uuid.
  const seedHostUuid = searchParams.get('host')
  const seedStructureUuid = searchParams.get('structure')
  return (
    <NewTransferWizard
      key={seedStructureUuid ?? seedHostUuid ?? 'blank'}
      seedStructureUuid={seedStructureUuid}
      seedHostUuid={seedHostUuid}
    />
  )
}

function NewTransferWizard({
  seedStructureUuid,
  seedHostUuid,
}: {
  seedStructureUuid: string | null
  seedHostUuid: string | null
}) {
  const navigate = useNavigate()
  const [state, setState] = useState<TransferWizardState>(() => {
    // Structure-scope deep link (preferred - gives per-row eligibility
    // chips). Sent by the warranty record page's Transfer action when
    // the registration lives on a Structure.
    if (seedStructureUuid) {
      return {
        ...initialState,
        selectedStructureUuid: seedStructureUuid,
        stepKey: 'eligibility',
      }
    }
    // Host-scope deep link (legacy / vehicle-style tenants whose host
    // has no sub-structures).
    if (seedHostUuid) {
      return {
        ...initialState,
        selectedHostUuid: seedHostUuid,
        stepKey: 'eligibility',
      }
    }
    return initialState
  })

  const update = useCallback(
    (payload: Partial<TransferWizardState>) =>
      setState((prev) => ({ ...prev, ...payload })),
    [],
  )

  // Data-driven step set. ``requires_fee`` / ``requires_documentation``
  // come from the tenant's configured TransferReason (kind='type') rows,
  // never a hardcoded transfer-type code - see ``useTransferTypes``.
  const transferTypesQuery = useTransferTypes()
  const apiTransferTypes = useMemo(
    () => transferTypesQuery.data ?? [],
    [transferTypesQuery.data],
  )
  // Only trust the API catalogue once it has actually loaded something -
  // while loading (or if a tenant genuinely has zero configured types)
  // fall back to the static list and keep every step, matching the
  // wizard's original behaviour.
  const usingApiTransferTypes =
    !transferTypesQuery.isLoading && apiTransferTypes.length > 0

  const typeOptions = useMemo(
    () =>
      usingApiTransferTypes
        ? apiTransferTypes.map((t) => ({ value: t.code, label: t.label }))
        : TRANSFER_TYPES,
    [usingApiTransferTypes, apiTransferTypes],
  )

  const selectedTransferType: TransferReason | null = usingApiTransferTypes
    ? (apiTransferTypes.find((t) => t.code === state.transferType) ?? null)
    : null

  // Absent flag (or no data yet) defaults to "required" - fee/document
  // steps only drop out on an EXPLICIT false from the server.
  const requiresFee = usingApiTransferTypes
    ? selectedTransferType?.requires_fee !== false
    : true
  const requiresDocumentation = usingApiTransferTypes
    ? selectedTransferType?.requires_documentation !== false
    : true

  const steps = useMemo<StepDef[]>(
    () =>
      ALL_STEP_DEFS.filter((s) => {
        if (s.key === 'fee') return requiresFee
        if (s.key === 'documents') return requiresDocumentation
        return true
      }),
    [requiresFee, requiresDocumentation],
  )

  // If the selected transfer type changes such that the wizard's current
  // step no longer exists (e.g. an agent backs up from Review and swaps
  // Sale for Name Change, dropping Fee + Documents), snap forward to the
  // nearest step that still applies instead of rendering a step that was
  // just removed from the stepper.
  useEffect(() => {
    if (steps.some((s) => s.key === state.stepKey)) return
    const fromIndex = ALL_STEP_DEFS.findIndex((s) => s.key === state.stepKey)
    const fallback =
      ALL_STEP_DEFS.slice(fromIndex + 1).find((s) =>
        steps.some((st) => st.key === s.key),
      ) ?? steps[steps.length - 1]
    setState((prev) => ({ ...prev, stepKey: fallback.key }))
  }, [steps, state.stepKey])

  const next = useCallback(
    () =>
      setState((prev) => {
        const idx = steps.findIndex((s) => s.key === prev.stepKey)
        const nextIdx = Math.min(idx + 1, steps.length - 1)
        return { ...prev, stepKey: steps[nextIdx].key }
      }),
    [steps],
  )

  const back = useCallback(
    () =>
      setState((prev) => {
        const idx = steps.findIndex((s) => s.key === prev.stepKey)
        const prevIdx = Math.max(idx - 1, 0)
        return { ...prev, stepKey: steps[prevIdx].key }
      }),
    [steps],
  )

  const goTo = useCallback(
    (stepKey: StepKey) => setState((prev) => ({ ...prev, stepKey })),
    [],
  )

  // Fetch holdings for fee/review steps (shared across later steps).
  //
  // Structure-scope mode pulls the FULL set of holdings on the
  // structure (no status filter) so the per-row eligibility chips can
  // surface cancelled / non-transferable registrations with a reason.
  // The eligible-only auto-selection (a few lines down) keeps the
  // existing "all transferable holdings move with the property"
  // behaviour intact.
  //
  // Host-scope mode keeps the legacy ``status=active`` filter - it's
  // the path used by tenants whose hosts have no sub-structures
  // (cars, yachts, single-building properties).
  const holdingsParams = useMemo(() => {
    const p: Record<string, string> = { page_size: '50' }
    if (state.selectedStructureUuid) {
      p.structure_uuid = state.selectedStructureUuid
    } else if (state.selectedHostUuid) {
      // No status filter when entering via the warranty page's
      // Transfer button - the user came from a real holding (which
      // may itself be cancelled, e.g. they want to start a new
      // transfer attempt). The eligibility endpoint surfaces the
      // per-row reason.
      p.host_uuid = state.selectedHostUuid
    } else {
      p.status = 'active'
    }
    return p
  }, [state.selectedHostUuid, state.selectedStructureUuid])
  const { data: holdingsData } = useHoldings(holdingsParams)

  // Backfill the host display from the first loaded holding when the
  // wizard was deep-linked with ``?host=<uuid>`` - we skipped the ZIP
  // search so ``hostDisplay`` would otherwise stay null and later
  // review rows would render "-".
  useEffect(() => {
    if (state.hostDisplay) return
    const firstHost = holdingsData?.results?.[0]?.host
    if (!firstHost) return
    const display =
      firstHost.address?.display ||
      [
        firstHost.address?.value.address_line_1,
        firstHost.address?.value.postal_code,
      ]
        .filter(Boolean)
        .join(' \u00b7 ')
    if (!display) return
    setState((prev) => (prev.hostDisplay ? prev : { ...prev, hostDisplay: display }))
  }, [holdingsData, state.hostDisplay])

  // Eligibility preflight - lifted up to the wizard so steps 1, 2, and
  // review all see the same server-authoritative result. Fires whenever
  // (holding, transfer_type, closing_date) is fully specified.
  const preflight = useEligibilityPreflight()
  const [preflightResult, setPreflightResult] =
    useState<EligibilityPreflightResult | null>(null)
  const firstHoldingUuid = state.selectedHoldingUuids[0] ?? null
  const preflightClosingDate = state.propertyClosingDate || null
  const preflightTransferType = state.transferType
  useEffect(() => {
    if (!firstHoldingUuid || !preflightClosingDate) {
      setPreflightResult(null)
      return
    }
    let cancelled = false
    preflight
      .mutateAsync({
        holding_uuid: firstHoldingUuid,
        transfer_type: preflightTransferType,
        ownership_change_date: preflightClosingDate,
      })
      .then((r) => {
        if (!cancelled) setPreflightResult(r)
      })
      .catch(() => {
        // Preflight is advisory; the real 409 still fires on submit.
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstHoldingUuid, preflightClosingDate, preflightTransferType])

  const createTransfer = useCreateTransfer()

  const handleSubmit = useCallback(async () => {
    // A property subject is required. The wizard can be scoped by either
    // a structure (property record deep-link) or a host (legacy / vehicle
    // entry); the payload below sends whichever id is set. Guarding on
    // host alone wrongly blocked structure-scoped transfers.
    if (!state.selectedHostUuid && !state.selectedStructureUuid) {
      toast.error('Please select a property')
      return
    }
    if (state.selectedHoldingUuids.length === 0) {
      toast.error('Please select at least one registration')
      return
    }
    if (state.interactionNotes.trim().length < 10) {
      toast.error('Please add interaction notes (10+ characters)')
      return
    }

    // §6: the docs recipient is a distinct person (not the new owner) when the
    // "To" address differs from new_owner_email - then their entered name is
    // sent so the seeded contact is named correctly and made the main contact.
    const docsRecipientIsNew =
      state.emailUploadLink &&
      state.docsEmailRecipient.trim() !== '' &&
      state.docsEmailRecipient.trim().toLowerCase() !==
        state.newOwnerEmail.trim().toLowerCase()

    // Build the payload once so retry-with-override sends an identical
    // request apart from the override_accepted flip. ``scope`` is
    // derived server-side from whichever id was supplied; we just pick
    // structure_uuid when the wizard was deep-linked from a Structure
    // and fall back to host_uuid for legacy / vehicle-style entries.
    const basePayload: CreateTransferPayload = {
      ...(state.selectedStructureUuid
        ? { structure_uuid: state.selectedStructureUuid }
        : { host_uuid: state.selectedHostUuid ?? undefined }),
      new_owner_first_name: state.newOwnerFirstName,
      new_owner_last_name: state.newOwnerLastName,
      new_owner_email: state.newOwnerEmail || undefined,
      new_owner_phone: state.newOwnerPhone || undefined,
      // If the agent edited the docs-request recipient in the Documents step,
      // seed it as a contact. The server no-ops when it matches new_owner_email
      // (or is blank). §6: when it is a DISTINCT person, the entered name is
      // sent so the contact is named correctly and set as the main contact.
      additional_owner_email: state.docsEmailRecipient || undefined,
      additional_owner_first_name: docsRecipientIsNew
        ? state.docsRecipientFirstName || undefined
        : undefined,
      additional_owner_last_name: docsRecipientIsNew
        ? state.docsRecipientLastName || undefined
        : undefined,
      // §12/§14: emailing the new owner (rather than uploading proof at intake)
      // moves the transfer to Awaiting Customer Information and starts reminders.
      request_documents_on_create: state.emailUploadLink,
      // §10: carry the reviewer's edited note + Cc so the documents-request email
      // actually sent matches the draft shown in the wizard.
      document_request_message: state.emailUploadLink
        ? state.docsEmailBody.trim() || undefined
        : undefined,
      document_request_cc: state.emailUploadLink
        ? state.docsEmailCc
            .split(',')
            .map((e) => e.trim())
            .filter((e) => e !== '')
        : undefined,
      setup_reminder: state.emailUploadLink,
      ownership_change_date: state.propertyClosingDate || undefined,
      transfer_type: state.transferType,
      // This transfer type has no Fee step (``requires_fee === false``),
      // so no fee is applied/collected - send the "no fee" shape
      // regardless of whatever the fields happened to hold from a
      // previous type selection.
      fee_waived: requiresFee ? state.feeWaived : false,
      fee_waived_reason: requiresFee
        ? state.feeWaivedReason || undefined
        : undefined,
      // NOTE: backend expects a real PaymentMethod enum value
      // (``check`` | ``online``). If the fee is waived - or this
      // transfer type doesn't require a fee at all - the field is
      // blank, never a stale payment method from an earlier step.
      payment_method: requiresFee && !state.feeWaived ? state.paymentMethod : '',
      interaction_notes: state.interactionNotes,
      override_accepted: state.override100DayFlag || undefined,
      override_reason: state.override100DayFlag
        ? state.overrideReason
        : undefined,
    }

    try {
      const result = await createTransfer.mutateAsync(basePayload)
      toast.success('Transfer submitted successfully')
      navigate(`/transfers/${result.uuid}`)
    } catch (err: unknown) {
      // Backend emits a 409-shaped APIResponse when eligibility evaluator
      // returned BLOCK violations AND override_accepted was false. Shape:
      //   { status, message, result: { violations: [...], requires_exception: true } }
      // We surface the violations directly and offer retry-with-override
      // so agents don't have to re-fill the wizard.
      const violations = extractEligibilityViolations(err)
      if (violations && violations.length > 0) {
        const canOverride = violations.every((v) => v.severity !== 'BLOCK') === false
        const summary = violations
          .map((v) => `• ${v.message}`)
          .slice(0, 4)
          .join('\n')
        if (canOverride && !basePayload.override_accepted) {
          toast.error(
            `Eligibility check failed:\n${summary}\n\nConfirm override in the warning modal to proceed.`,
            { duration: 8000 },
          )
          // Nudge the user back to the wizard's override step so they can
          // acknowledge and re-submit. The existing override modal flow
          // sets override100DayFlag on confirm.
          update({ override100DayFlag: false })
        } else {
          toast.error(
            `Transfer blocked:\n${summary}`,
            { duration: 8000 },
          )
        }
        return
      }
      toastApiError(err, 'Failed to submit transfer')
    }
  }, [state, createTransfer, navigate, update, requiresFee])

  const stepContent = useMemo(() => {
    switch (state.stepKey) {
      case 'property':
        return (
          <StepPropertyLookup state={state} update={update} next={next} />
        )
      case 'eligibility':
        return (
          <StepEligibility
            state={state}
            update={update}
            next={next}
            back={back}
            preflight={preflightResult}
            preflightLoading={preflight.isPending}
          />
        )
      case 'owner':
        return (
          <StepOwnerAndClosing
            state={state}
            update={update}
            next={next}
            back={back}
            preflight={preflightResult}
            preflightLoading={preflight.isPending}
            typeOptions={typeOptions}
          />
        )
      case 'documents':
        return (
          <StepDocuments state={state} update={update} next={next} back={back} />
        )
      case 'fee':
        return (
          <StepFee
            state={state}
            update={update}
            next={next}
            back={back}
            baseFee={
              preflightResult?.resolved_policy
                ? Number(preflightResult.resolved_policy.fee_amount)
                : 0
            }
          />
        )
      case 'review':
        return (
          <StepReview
            state={state}
            update={update}
            back={back}
            onSubmit={handleSubmit}
            isSubmitting={createTransfer.isPending}
            preflight={preflightResult}
            preflightLoading={preflight.isPending}
            typeOptions={typeOptions}
            requiresFee={requiresFee}
            requiresDocumentation={requiresDocumentation}
          />
        )
      default:
        return null
    }
  }, [
    state,
    update,
    next,
    back,
    handleSubmit,
    createTransfer.isPending,
    preflightResult,
    preflight.isPending,
    typeOptions,
    requiresFee,
    requiresDocumentation,
  ])

  return (
    <AppShell>
      <div className="flex min-h-[calc(100vh-48px)] flex-col bg-[var(--bg)]">
        {/* Header */}
        <div className="border-b-[0.5px] border-b-[var(--rule)]">
          <div className="page-container py-5 pb-4">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h1 className="clara-title m-0">
                  New Transfer
                </h1>
                <p className="mt-1 text-[12px] text-[var(--ink-3)]">
                  Transfer warranty ownership to a new property owner
                </p>
              </div>
              <Button
                variant="ghost"
                onClick={() => navigate('/transfers')}
                className="text-[11px] uppercase tracking-[0.1em]"
              >
                Cancel
              </Button>
            </div>

            {/* Stepper - the step SET is dynamic (see ``steps`` above), so
                indices/labels/navigation are all derived from it rather
                than a fixed count. */}
            <ol
              aria-label="New transfer wizard steps"
              className="mb-0 flex max-w-[820px] items-center"
            >
              {steps.map((step, i) => {
                const currentIndex = steps.findIndex(
                  (s) => s.key === state.stepKey,
                )
                const isComplete = i < currentIndex
                const isCurrent = i === currentIndex
                const statusWord = isComplete
                  ? 'completed'
                  : isCurrent
                    ? 'current'
                    : 'not started'
                return (
                  <li key={step.key} className="flex items-center">
                    <Button
                      variant="ghost"
                      onClick={() => {
                        if (i < currentIndex) goTo(step.key)
                      }}
                      aria-current={isCurrent ? 'step' : undefined}
                      aria-label={`Step ${i + 1} of ${steps.length}: ${step.label} - ${statusWord}`}
                      className={`flex h-auto items-center gap-1.5 p-0 hover:bg-transparent ${
                        i < currentIndex ? 'cursor-pointer' : 'cursor-default'
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[9px] font-normal ${
                          i <= currentIndex
                            ? 'bg-[var(--green)] text-white'
                            : 'border-[0.5px] border-[var(--rule-strong)] bg-transparent text-[var(--ink-3)]'
                        }`}
                      >
                        {i < currentIndex ? '\u2713' : i + 1}
                      </span>
                      <span
                        className={`hidden whitespace-nowrap text-[10px] uppercase tracking-[0.12em] sm:inline ${
                          i <= currentIndex
                            ? 'text-[var(--ink)]'
                            : 'text-[var(--ink-3)]'
                        }`}
                      >
                        {step.label}
                      </span>
                    </Button>
                    {i < steps.length - 1 && (
                      <div
                        aria-hidden="true"
                        className="mx-1 h-px w-5 shrink-0 bg-[var(--rule-strong)] sm:mx-2 sm:w-8"
                      />
                    )}
                  </li>
                )
              })}
            </ol>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="page-container py-8">{stepContent}</div>
        </div>
      </div>
    </AppShell>
  )
}
